import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { API, useAuth } from "../App";
import { ArrowLeft, User, Mail, Shield, Crown, Calendar, Lock, AlertCircle, ChevronDown, Loader2, Trash2 } from "lucide-react";
import { useIsWebView } from "../hooks/useIsWebView";
import FuturaIcon from "../components/FuturaIcon";
import { useLanguage } from "../i18n/LanguageContext";
import AchievementsSection from "../components/AchievementsSection";
import { LanguageSelector } from "../components/LanguageSelector";
import axios from "axios";

const Profile = () => {
  const navigate = useNavigate();
  const isWebView = useIsWebView();
  const { user, checkAuth } = useAuth();
  const { t } = useLanguage();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Change password state
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [pwError, setPwError] = useState("");
  const [pwSuccess, setPwSuccess] = useState("");
  const [pwLoading, setPwLoading] = useState(false);

  // Cancel subscription
  const [showCancelSection, setShowCancelSection] = useState(false);
  const [cancelConfirm, setCancelConfirm] = useState("");
  const [cancelLoading, setCancelLoading] = useState(false);

  // Delete account
  const [showDeleteAccount, setShowDeleteAccount] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await axios.get(`${API}/auth/profile`, { withCredentials: true });
        setProfile(res.data);
      } catch (err) {
        navigate("/login");
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwError("");
    setPwSuccess("");
    if (newPassword.length < 6) { setPwError(t("profile_pw_min")); return; }
    if (!/^[A-Za-z0-9]+$/.test(newPassword)) { setPwError(t("register_password_alphanumeric")); return; }
    if (newPassword !== confirmPassword) { setPwError(t("forgot_passwords_mismatch")); return; }
    setPwLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, { withCredentials: true });
      setPwSuccess(t("profile_pw_success"));
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setShowChangePassword(false);
    } catch (err) {
      setPwError(err.response?.data?.detail || "Error");
    } finally {
      setPwLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (cancelConfirm !== "CANCELAR") return;
    setCancelLoading(true);
    try {
      await axios.post(`${API}/auth/cancel-subscription`, {}, { withCredentials: true });
      await checkAuth();
      navigate("/dashboard");
    } catch (err) {
      setCancelLoading(false);
    }
  };

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    setDeleteError("");
    setDeleteLoading(true);
    try {
      await axios.post(`${API}/auth/delete-account`, {
        password: deletePassword
      }, { withCredentials: true });
      localStorage.clear();
      window.location.href = "/";
    } catch (err) {
      setDeleteError(err.response?.data?.detail || "Error al eliminar la cuenta");
      setDeleteLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F7] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#002FA7] animate-spin" />
      </div>
    );
  }

  const isPaid = profile?.plan !== "descubre";
  const planColors = { descubre: "bg-emerald-500/10 text-emerald-600", decide: "bg-blue-500/10 text-blue-600", avanza: "bg-amber-500/10 text-amber-600" };
  const planColor = planColors[profile?.plan] || planColors.descubre;

  return (
    <div className="min-h-screen bg-[#F5F5F7]" data-testid="profile-page">
      <header className="bg-white border-b border-[#E5E5EA] sticky top-0 z-20">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-[#6B7280] hover:text-[#111111] transition-colors">
            <ArrowLeft className="w-5 h-5" /><span className="font-medium">{t("nav_back")}</span>
          </Link>
          <div className="flex items-center gap-3">
            <LanguageSelector />
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#002FA7] to-[#5B21B6] flex items-center justify-center p-1">
              <FuturaIcon className="w-full h-full text-white" />
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8 sm:py-12">
        {/* Profile header */}
        <div className="flex items-center gap-5 mb-8">
          <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-[#002FA7] to-[#5B21B6] flex items-center justify-center flex-shrink-0">
            {profile?.picture ? (
              <img src={profile.picture} alt="" className="w-full h-full rounded-3xl object-cover" />
            ) : (
              <span className="text-3xl font-['Outfit'] font-bold text-white">{profile?.name?.charAt(0)?.toUpperCase() || "U"}</span>
            )}
          </div>
          <div>
            <h1 className="font-['Outfit'] text-2xl font-semibold text-[#111111]">{profile?.name}</h1>
            <span className={`inline-block text-xs font-semibold uppercase tracking-wide px-3 py-1 rounded-full mt-1 ${planColor}`}>
              {profile?.plan_name || profile?.plan}
            </span>
          </div>
        </div>

        {/* Profile info cards */}
        <div className="space-y-4 mb-8">
          <div className="bg-white rounded-2xl p-5 border border-[#E5E5EA]" data-testid="profile-email-card">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-[#002FA7]/10 flex items-center justify-center"><Mail className="w-5 h-5 text-[#002FA7]" /></div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-[#6B7280] uppercase tracking-wide">{t("profile_email")}</p>
                <p className="font-medium text-[#111111] truncate">{profile?.email}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-5 border border-[#E5E5EA]" data-testid="profile-auth-card">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-[#5B21B6]/10 flex items-center justify-center"><Shield className="w-5 h-5 text-[#5B21B6]" /></div>
              <div className="flex-1">
                <p className="text-xs text-[#6B7280] uppercase tracking-wide">{t("profile_auth_method")}</p>
                <p className="font-medium text-[#111111]">{t("profile_auth_email")}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-5 border border-[#E5E5EA]" data-testid="profile-plan-card">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-[#10B981]/10 flex items-center justify-center"><Crown className="w-5 h-5 text-[#10B981]" /></div>
              <div className="flex-1">
                <p className="text-xs text-[#6B7280] uppercase tracking-wide">{t("profile_plan")}</p>
                <p className="font-medium text-[#111111]">{profile?.plan_name || profile?.plan}</p>
              </div>
              {!isPaid && !isWebView && (
                <Button onClick={() => navigate("/pricing")} variant="outline" size="sm" className="text-[#002FA7] border-[#002FA7]/20 hover:bg-[#002FA7]/5 rounded-xl" data-testid="profile-upgrade-btn">
                  {t("profile_upgrade")}
                </Button>
              )}
              {!isPaid && isWebView && (
                <span className="text-xs text-[#6B7280]" data-testid="webview-sub-text-profile">{t("webview_sub_managed")}</span>
              )}
            </div>
          </div>

          <div className="bg-white rounded-2xl p-5 border border-[#E5E5EA]" data-testid="profile-date-card">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-[#F59E0B]/10 flex items-center justify-center"><Calendar className="w-5 h-5 text-[#F59E0B]" /></div>
              <div className="flex-1">
                <p className="text-xs text-[#6B7280] uppercase tracking-wide">{t("profile_member_since")}</p>
                <p className="font-medium text-[#111111]">
                  {profile?.created_at ? new Date(profile.created_at).toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" }) : "-"}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Change Password section */}
          <div className="bg-white rounded-2xl border border-[#E5E5EA] mb-4 overflow-hidden" data-testid="profile-change-password">
            <button
              onClick={() => { setShowChangePassword(!showChangePassword); setPwError(""); setPwSuccess(""); }}
              className="w-full flex items-center justify-between p-5 text-left hover:bg-[#F5F5F7]/50 transition-colors"
              data-testid="profile-change-password-toggle"
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-[#EF4444]/10 flex items-center justify-center"><Lock className="w-5 h-5 text-[#EF4444]" /></div>
                <div>
                  <p className="font-medium text-[#111111]">{t("profile_change_password")}</p>
                  <p className="text-xs text-[#6B7280]">{t("profile_change_password_sub")}</p>
                </div>
              </div>
              <ChevronDown className={`w-5 h-5 text-[#6B7280] transition-transform ${showChangePassword ? "rotate-180" : ""}`} />
            </button>
            {showChangePassword && (
              <div className="px-5 pb-5 border-t border-[#E5E5EA]">
                {pwSuccess && <div className="mt-4 p-3 bg-[#10B981]/10 text-[#10B981] rounded-xl text-sm">{pwSuccess}</div>}
                {pwError && <div className="mt-4 p-3 bg-[#EF4444]/10 text-[#EF4444] rounded-xl text-sm flex items-center gap-2"><AlertCircle className="w-4 h-4 flex-shrink-0" />{pwError}</div>}
                <form onSubmit={handleChangePassword} className="mt-4 space-y-4">
                  <div className="space-y-2">
                    <Label className="text-sm text-[#111111]">{t("profile_current_password")}</Label>
                    <Input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} className="h-11 bg-[#F5F5F7] border-[#E5E5EA] rounded-xl" required data-testid="profile-current-password" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-[#111111]">{t("reset_new_password")}</Label>
                    <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value.replace(/[^A-Za-z0-9]/g, ""))} className="h-11 bg-[#F5F5F7] border-[#E5E5EA] rounded-xl" required minLength={6} pattern="[A-Za-z0-9]{6,}" data-testid="profile-new-password" />
                    <p className="text-xs text-[#6B7280]">{t("register_password_hint")}</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-[#111111]">{t("forgot_confirm_password")}</Label>
                    <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value.replace(/[^A-Za-z0-9]/g, ""))} className="h-11 bg-[#F5F5F7] border-[#E5E5EA] rounded-xl" required minLength={6} pattern="[A-Za-z0-9]{6,}" data-testid="profile-confirm-password" />
                  </div>
                  <Button type="submit" disabled={pwLoading} className="bg-[#002FA7] hover:bg-[#001d6a] text-white rounded-xl h-11" data-testid="profile-save-password">
                    {pwLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("profile_save_password")}
                  </Button>
                </form>
              </div>
            )}
          </div>

        {/* Cancel Subscription - deliberately subtle */}
        {isPaid && (
          <div className="mt-16 pt-8 border-t border-[#E5E5EA]/50">
            <button
              onClick={() => setShowCancelSection(!showCancelSection)}
              className="text-xs text-[#6B7280]/40 hover:text-[#6B7280]/70 transition-colors"
              data-testid="profile-cancel-toggle"
            >
              {t("profile_manage_subscription")}
            </button>
            {showCancelSection && (
              <div className="mt-4 bg-white rounded-2xl border border-[#E5E5EA] p-5 animate-fade-in" data-testid="profile-cancel-section">
                <p className="text-sm text-[#6B7280] mb-4">{t("profile_cancel_warning")}</p>
                <div className="space-y-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-[#6B7280]">{t("profile_cancel_confirm_label")}</Label>
                    <Input
                      value={cancelConfirm}
                      onChange={(e) => setCancelConfirm(e.target.value)}
                      placeholder="CANCELAR"
                      className="h-10 bg-[#F5F5F7] border-[#E5E5EA] rounded-xl text-sm font-mono"
                      data-testid="profile-cancel-input"
                    />
                  </div>
                  <Button
                    onClick={handleCancelSubscription}
                    disabled={cancelConfirm !== "CANCELAR" || cancelLoading}
                    variant="outline"
                    className="text-[#EF4444] border-[#EF4444]/20 hover:bg-[#EF4444]/5 rounded-xl text-sm h-10"
                    data-testid="profile-cancel-btn"
                  >
                    {cancelLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("profile_cancel_button")}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Achievements */}
        <AchievementsSection />

        {/* Delete Account - at the very bottom */}
        <div className="mt-8 pt-6 border-t border-[#E5E5EA]/50">
          <button
            onClick={() => { setShowDeleteAccount(!showDeleteAccount); setDeleteError(""); }}
            className="text-xs text-[#EF4444]/40 hover:text-[#EF4444]/70 transition-colors flex items-center gap-1.5"
            data-testid="profile-delete-toggle"
          >
            <Trash2 className="w-3 h-3" />
            {t("profile_delete_account")}
          </button>
          {showDeleteAccount && (
            <div className="mt-4 bg-white rounded-2xl border border-[#EF4444]/20 p-5 animate-fade-in" data-testid="profile-delete-section">
              <p className="text-sm text-[#EF4444]/80 font-medium mb-2">{t("profile_delete_warning_title")}</p>
              <p className="text-xs text-[#6B7280] mb-4">{t("profile_delete_warning_text")}</p>
              {deleteError && (
                <div className="mb-4 p-3 bg-[#EF4444]/10 text-[#EF4444] rounded-xl text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />{deleteError}
                </div>
              )}
              <form onSubmit={handleDeleteAccount} className="space-y-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-[#6B7280]">{t("profile_delete_password_label")}</Label>
                    <Input
                      type="password"
                      value={deletePassword}
                      onChange={(e) => setDeletePassword(e.target.value)}
                      className="h-10 bg-[#F5F5F7] border-[#E5E5EA] rounded-xl text-sm"
                      required
                      data-testid="profile-delete-password"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={deleteLoading || !deletePassword}
                    className="bg-[#EF4444] hover:bg-[#DC2626] text-white rounded-xl text-sm h-10"
                    data-testid="profile-delete-confirm-btn"
                  >
                    {deleteLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("profile_delete_confirm")}
                  </Button>
                </form>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Profile;
